// Local compute processes (miner, local trainer). Services register here so the
// window manager can apply lifecycle rules (close with active work, shut down)
// and Task Manager can list what is actually running in this tab.

import { create } from 'zustand';
import type { AppId } from './apps';

export interface Process {
  id: string;
  app: AppId;
  name: string;
  active: boolean;
  detail: string; // short live status, e.g. "1.2 MH/s" or "step 120/600"
  startedAt: number | null;
  stop: () => Promise<void> | void;
}

interface ProcStore {
  procs: Record<string, Process>;
  upsert: (p: Process) => void;
  patch: (id: string, p: Partial<Process>) => void;
}

export const useProcesses = create<ProcStore>((set) => ({
  procs: {},
  upsert: (p) => set((s) => ({ procs: { ...s.procs, [p.id]: p } })),
  patch: (id, p) =>
    set((s) => {
      const cur = s.procs[id];
      if (!cur) return s;
      // Skip no-op updates so subscribers don't re-render on every tick.
      let same = true;
      for (const k of Object.keys(p) as (keyof Process)[]) if (cur[k] !== p[k]) same = false;
      return same ? s : { procs: { ...s.procs, [id]: { ...cur, ...p } } };
    }),
}));

export const activeProcessesFor = (app: AppId) => Object.values(useProcesses.getState().procs).filter((p) => p.app === app && p.active);
export const activeProcesses = () => Object.values(useProcesses.getState().procs).filter((p) => p.active);

export async function stopAll() {
  await Promise.allSettled(activeProcesses().map((p) => p.stop()));
}
